/**
 * Throttle for the soft "update available" prompt so it isn't nag-ware.
 *
 * Stores the recommended version the user last dismissed plus a timestamp. The
 * prompt is suppressed for THROTTLE_WINDOW_MS after a dismissal of the *same*
 * recommended version — when a newer recommended version ships, it shows again.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';

const STORAGE_KEY = 'update_prompt_dismissed';
const THROTTLE_WINDOW_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

interface DismissalRecord {
  version: string;
  dismissedAt: number;
}

async function readRecord(): Promise<DismissalRecord | null> {
  try {
    const raw =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? localStorage.getItem(STORAGE_KEY)
        : await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DismissalRecord;
    if (typeof parsed?.version !== 'string' || typeof parsed?.dismissedAt !== 'number') {
      return null;
    }
    return parsed;
  } catch (error) {
    if (__DEV__) logger.warn('updatePromptThrottle: read failed', { error });
    return null;
  }
}

/**
 * True when the soft prompt should be shown for this recommended version
 * (i.e. not dismissed for it within the throttle window).
 */
export async function shouldShowUpdatePrompt(recommendedVersion: string): Promise<boolean> {
  const record = await readRecord();
  if (!record) return true;
  if (record.version !== recommendedVersion) return true;
  return Date.now() - record.dismissedAt >= THROTTLE_WINDOW_MS;
}

/** Record that the user saw/dismissed the prompt for this recommended version. */
export async function markUpdatePromptDismissed(recommendedVersion: string): Promise<void> {
  const record: DismissalRecord = {
    version: recommendedVersion,
    dismissedAt: Date.now(),
  };
  try {
    const raw = JSON.stringify(record);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, raw);
      return;
    }
    await AsyncStorage.setItem(STORAGE_KEY, raw);
  } catch (error) {
    if (__DEV__) logger.warn('updatePromptThrottle: write failed', { error });
  }
}
